import { LiveUpdate } from "@capawesome/capacitor-live-update";
import { App, URLOpenListenerEvent } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { router } from "@/router";
import { getDeepLinkHref } from "./deep-link";
import { getEnvVar } from "./environment";
import { Storage } from "./storage";
import { PushNotificationService } from "./PushNotificationService";
const LIVE_UPDATE_CHANNEL = getEnvVar(
  "VITE_PUBLIC_LIVE_UPDATE_CHANNEL_VERSION",
);

const isDev = getEnvVar("VITE_PUBLIC_ENV") === "development";

const PENDING_PATH_KEY = "liveUpdatePendingPath";

/**
 * In-flight sync, shared by the startup sync and the resume listener.
 *
 * The plugin rejects with "Sync is already in progress." if two syncs overlap,
 * and they do overlap in practice: the resume listener is registered before the
 * startup sync finishes, and OAuth backgrounds then re-foregrounds the app
 * mid-flow, firing `resume` while startup sync is still running. Coalescing to
 * one promise means the second caller observes the first sync's result instead
 * of triggering a rejection.
 */
let inFlightSync: Promise<{ nextBundleId: string | null }> | null = null;

const syncLiveUpdate = async (channel: string) => {
  if (!inFlightSync) {
    inFlightSync = LiveUpdate.sync({ channel }).finally(() => {
      inFlightSync = null;
    });
  }
  return inFlightSync;
};

async function reloadWithPathPreserved() {
  const { pathname, search, hash } = window.location;
  const path = `${pathname}${search}${hash}`;
  if (path && path !== "/") {
    await Storage.setItem(PENDING_PATH_KEY, path);
  }
  await LiveUpdate.reload();
}

async function restorePendingPath() {
  const path = await Storage.getItem(PENDING_PATH_KEY);
  if (!path) {
    return;
  }
  await Storage.removeItem(PENDING_PATH_KEY);
  // `href` parses the saved query string and hash; `to` would treat them as
  // part of the pathname and drop them.
  router.navigate({ href: path });
}

export async function initializeApp() {
  // Check if it's not web
  if (Capacitor.getPlatform() === "web") {
    return;
  }

  PushNotificationService.setupPushListeners();

  await restorePendingPath();

  // Register event to fire each time user resumes the app
  App.addListener("resume", async () => {
    try {
      const shouldReloadApp = await Storage.getItem("shouldReloadApp");
      if (shouldReloadApp === "true") {
        console.log("Reloading app on resume");
        await Storage.removeItem("shouldReloadApp");
        await reloadWithPathPreserved();
      } else {
        const result = await syncLiveUpdate(LIVE_UPDATE_CHANNEL);
        console.log("Syncing app", result.nextBundleId);
        await Storage.setItem(
          "shouldReloadApp",
          String(result.nextBundleId != null),
        );
      }
    } catch (error) {
      // If offline or error occurs, continue without sync
      console.log("Failed to sync updates on resume:", error);
    }
  });

  App.addListener("appUrlOpen", (event: URLOpenListenerEvent) => {
    const href = getDeepLinkHref(event.url);
    console.log("Universal link", href);
    if (!href) {
      return;
    }
    router.navigate({ href });
  });

  // A failed startup sync must not stop us from reaching LiveUpdate.ready():
  // the plugin treats an unconfirmed bundle as bad and rolls back on next
  // launch, so an offline start could otherwise discard a healthy bundle.
  try {
    const result = await syncLiveUpdate(
      isDev ? "development" : LIVE_UPDATE_CHANNEL,
    );
    if (result.nextBundleId) {
      console.log("Reloading app", result.nextBundleId);
      await reloadWithPathPreserved();
    }
  } catch (error) {
    console.log("Failed to sync updates on startup:", error);
  }

  await LiveUpdate.ready();
}
